import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useValidationMessage } from "@/shared/lib/i18n/validation-message.ts";
import { Machine, MachineSettingsInput } from "@/machines/types/machine.ts";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { Switch } from "@/ui/components/ui/switch";
import { useUpdateMachineSettings } from "@/machines/api/queries/machines.queries.ts";
import toast from "react-hot-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/components/ui/card";
import { Separator } from "@/ui/components/ui/separator";

interface MachineSettingsFormProps {
  machine: Machine;
}

const MachineSettingsForm = ({ machine }: MachineSettingsFormProps) => {
  const { t } = useTranslation("machines");
  const updateSettings = useUpdateMachineSettings();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<MachineSettingsInput>({
    defaultValues: {
      name: machine.name || "",
      bucket_width_mm: machine.bucket_width_mm,
      loading_time_target_s: machine.loading_time_target_s,
      waiting_time_target_s: machine.waiting_time_target_s,
      buckets_per_truck_target: machine.buckets_per_truck_target,
      is_active: machine.is_active ?? true,
    },
  });
  const validationMessage = useValidationMessage();

  const isActive = watch("is_active");

  const onSubmit = async (data: MachineSettingsInput) => {
    try {
      await updateSettings.mutateAsync({
        id: machine.id,
        settings: data,
      });
      toast.success(t("settingsForm.toast.success"));
    } catch (error) {
      console.error("Failed to update machine settings:", error);
      toast.error(t("settingsForm.toast.error"));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settingsForm.basicInfo.title")}</CardTitle>
          <CardDescription>
            {t("settingsForm.basicInfo.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="device_mxid">{t("settingsForm.fields.deviceIdLabel")}</Label>
            <Input
              id="device_mxid"
              value={machine.device_mxid}
              disabled
              className="bg-muted"
            />
            <p className="text-sm text-muted-foreground">
              {t("settingsForm.fields.deviceIdHint")}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">{t("settingsForm.fields.nameLabel")}</Label>
            <Input
              id="name"
              {...register("name", {
                required: "validation.machineNameRequired",
              })}
              placeholder={t("settingsForm.fields.namePlaceholder")}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{validationMessage(errors.name.message)}</p>
            )}
          </div>

          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-0.5">
              <Label htmlFor="is_active">{t("settingsForm.fields.activeLabel")}</Label>
              <p className="text-sm text-muted-foreground">
                {t("settingsForm.fields.activeHint")}
              </p>
            </div>
            <Switch
              id="is_active"
              checked={isActive}
              onCheckedChange={(checked) => setValue("is_active", checked, { shouldDirty: true })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Machine Specifications */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settingsForm.specs.title")}</CardTitle>
          <CardDescription>
            {t("settingsForm.specs.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bucket_width_mm">{t("settingsForm.fields.bucketWidthLabel")}</Label>
            <Input
              id="bucket_width_mm"
              type="number"
              {...register("bucket_width_mm", {
                valueAsNumber: true,
                min: { value: 0, message: "validation.bucketWidthPositive" },
              })}
              placeholder={t("settingsForm.fields.bucketWidthPlaceholder")}
            />
            <p className="text-sm text-muted-foreground">
              {t("settingsForm.fields.bucketWidthHint")}
            </p>
            {errors.bucket_width_mm && (
              <p className="text-sm text-destructive">
                {validationMessage(errors.bucket_width_mm.message)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Performance Targets */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settingsForm.targets.title")}</CardTitle>
          <CardDescription>
            {t("settingsForm.targets.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="loading_time_target_s">{t("settingsForm.fields.loadingTargetLabel")}</Label>
            <Input
              id="loading_time_target_s"
              type="number"
              {...register("loading_time_target_s", {
                valueAsNumber: true,
                min: { value: 0, message: "validation.loadingTimePositive" },
              })}
              placeholder={t("settingsForm.fields.loadingTargetPlaceholder")}
            />
            <p className="text-sm text-muted-foreground">
              {t("settingsForm.fields.loadingTargetHint")}
            </p>
            {errors.loading_time_target_s && (
              <p className="text-sm text-destructive">
                {validationMessage(errors.loading_time_target_s.message)}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="waiting_time_target_s">{t("settingsForm.fields.waitingTargetLabel")}</Label>
            <Input
              id="waiting_time_target_s"
              type="number"
              {...register("waiting_time_target_s", {
                valueAsNumber: true,
                min: { value: 0, message: "validation.waitingTimePositive" },
              })}
              placeholder={t("settingsForm.fields.waitingTargetPlaceholder")}
            />
            <p className="text-sm text-muted-foreground">
              {t("settingsForm.fields.waitingTargetHint")}
            </p>
            {errors.waiting_time_target_s && (
              <p className="text-sm text-destructive">
                {validationMessage(errors.waiting_time_target_s.message)}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="buckets_per_truck_target">{t("settingsForm.fields.bucketsPerTruckLabel")}</Label>
            <Input
              id="buckets_per_truck_target"
              type="number"
              {...register("buckets_per_truck_target", {
                valueAsNumber: true,
                min: { value: 1, message: "validation.atLeastOneBucket" },
              })}
              placeholder={t("settingsForm.fields.bucketsPerTruckPlaceholder")}
            />
            <p className="text-sm text-muted-foreground">
              {t("settingsForm.fields.bucketsPerTruckHint")}
            </p>
            {errors.buckets_per_truck_target && (
              <p className="text-sm text-destructive">
                {validationMessage(errors.buckets_per_truck_target.message)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Separator />

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={!isDirty || updateSettings.isPending}
          onClick={() => window.location.reload()}
        >
          {t("settingsForm.actions.reset")}
        </Button>
        <Button
          type="submit"
          disabled={!isDirty || updateSettings.isPending}
        >
          {updateSettings.isPending ? t("settingsForm.actions.saving") : t("settingsForm.actions.submit")}
        </Button>
      </div>
    </form>
  );
};

export default MachineSettingsForm;
